import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  addChatMessageAttachments,
  fetchChatCommunityThread,
  postChatCommunityMessage,
  toggleChatCardReaction,
  toggleChatMessageReaction,
  deleteChatCommunityMessage,
  updateChatCommunityMessage,
} from '../services/chatCommunityService'
import type { CommunityThread } from '@/modules/comunidade/services/communityService'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { validateCommunityAttachmentFile } from '@/modules/comunidade/utils/communityAttachments'
import {
  appendEditedSuffixToHtml,
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '@/modules/comunidade/utils/communityMessageContent'
import { emitCommunityThreadNotificationsAutoEnabled } from '@/modules/comunidade/services/communityThreadNotificationsEvents'

export function useChatCommunityThread(
  chatId: number | null,
  currentUser: { name: string; avatarUrl?: string | null } | null,
  active: boolean,
) {
  const [loading, setLoading] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [loadedOnce, setLoadedOnce] = React.useState(false)
  const [thread, setThread] = React.useState<CommunityThread>({
    messages: [],
    cardReactions: [],
  })

  const load = React.useCallback(async () => {
    if (!chatId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    setLoading(true)
    try {
      const data = await fetchChatCommunityThread(chatId)
      setThread(data)
      setLoadedOnce(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useChatCommunityThread load error', error)
    } finally {
      setLoading(false)
    }
  }, [active, chatId])

  React.useEffect(() => {
    if (!chatId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    let alive = true
    ;(async () => {
      await load()
      if (!alive) return
    })()
    return () => {
      alive = false
    }
  }, [active, load, chatId])

  React.useEffect(() => {
    if (!chatId || !active) return

    let timer: any = null

    const scheduleReload = () => {
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
      timer = setTimeout(() => {
        load().catch(() => {
          // erros tratados dentro de load
        })
      }, 500)
    }

    const channel = supabase
      .channel(`community_chat_thread:${chatId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_community_message_reactions' },
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_community_reactions',
          filter: `chat_id=eq.${chatId}`,
        } as any,
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_community_messages',
          filter: `chat_id=eq.${chatId}`,
        } as any,
        () => scheduleReload(),
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // ignore
      }
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
    }
  }, [active, load, chatId])

  React.useEffect(() => {
    if (!chatId || !active) return

    const interval = setInterval(() => {
      load().catch(() => {
        // silencioso; proximo ciclo corrige
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [active, load, chatId])

  const sendMessage = React.useCallback(
    async (content: string, attachments?: File[]) => {
      if (!chatId || !currentUser || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      const html = prepared.html
      const files = (Array.isArray(attachments) ? attachments : [])
        .filter((file): file is File => !!file)
        .filter((file) => validateCommunityAttachmentFile(file).ok)

      if (!prepared.text && !files.length) return

      setSending(true)
      try {
        const messageId = await postChatCommunityMessage(chatId, currentUser, html)
        if (messageId) {
          emitCommunityThreadNotificationsAutoEnabled({
            contextType: 'chat',
            contextId: chatId,
          })
        }
        if (messageId && files.length) {
          const uploads = await Promise.all(
            files.map(async (file) => {
              const { url, bucket, path } = await uploadPublicFile(
                file,
                `chat-community/messages/${messageId}`,
              )
              return {
                url,
                storageBucket: bucket,
                storagePath: path,
                contentType: file.type || null,
                sizeBytes: file.size,
              }
            }),
          )
          await addChatMessageAttachments(messageId, uploads)
        }
        await load()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('send chat community message error', e)
      } finally {
        setSending(false)
      }
    },
    [active, chatId, currentUser, load],
  )

  const toggleMessageEmoji = React.useCallback(
    async (messageId: number, emoji: string) => {
      if (!chatId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          Number(msg.id) === id
            ? {
                ...msg,
                reactions: applyOptimisticReactionToggle(msg.reactions, trimmed),
              }
            : msg,
        ),
      }))

      void toggleChatMessageReaction(id, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useChatCommunityThread toggleMessageEmoji error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      })
    },
    [active, chatId, load],
  )

  const toggleCardEmoji = React.useCallback(
    async (emoji: string) => {
      if (!chatId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setThread((prev) => ({
        ...prev,
        cardReactions: applyOptimisticReactionToggle(prev.cardReactions, trimmed),
      }))

      void toggleChatCardReaction(chatId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useChatCommunityThread toggleCardEmoji error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      })
    },
    [active, chatId, load],
  )

  const deleteMessage = React.useCallback(
    async (messageId: number) => {
      if (!chatId || !active) return
      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.filter((msg) => Number(msg.id) !== id),
      }))

      try {
        await deleteChatCommunityMessage(id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useChatCommunityThread deleteMessage error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      }
    },
    [active, chatId, load],
  )

  const editMessage = React.useCallback(
    async (messageId: number, content: string) => {
      if (!chatId || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      if (!prepared.text) return

      const baseHtml = stripEditedSuffixFromHtml(prepared.html).html
      const finalHtml = appendEditedSuffixToHtml(baseHtml)
      if (!finalHtml) return

      try {
        const updated = await updateChatCommunityMessage(messageId, finalHtml)
        if (!updated) {
          await load()
          return
        }

        setThread((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: updated } : msg,
          ),
        }))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useChatCommunityThread editMessage error', error)
        await load()
      }
    },
    [active, chatId, load],
  )

  return {
    thread,
    loading,
    loadedOnce,
    sending,
    reload: load,
    sendMessage,
    toggleMessageEmoji,
    toggleCardEmoji,
    deleteMessage,
    editMessage,
  }
}
